import React from 'react';

import { tableStore } from '../../../stores/table-store';

/**
 * Fetches the named scripts from the static `/scripts/` catalog and uploads
 * them via the same `handleUpload` a real file-input change event would
 * drive.
 *
 * Shared between the click-to-add path (`ResourceLibPanel`) and the drag-
 * and-drop path (`table.tsx`'s native drop handler) so a Resource Library
 * item's scripts land on the solution the same way regardless of which
 * gesture added it — Project Bobcat (Resource Library drag-and-drop).
 */
export const uploadResourceLibScripts = async (
  scriptNames: string[],
  handleUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
): Promise<void> => {
  if (scriptNames.length === 0) return;
  const filePromises = scriptNames.map(async (name) => {
    const response = await fetch(`/scripts/${name}`);

    const contentType = response.headers.get('content-type');

    if (!response.ok || (contentType && contentType.includes('text/html'))) {
      console.error(`Script not found: ${name}`);
      return null;
    }

    const blob = await response.blob();
    return new File([blob], name, { type: 'text/x-python' });
  });

  const files = await Promise.all(filePromises);

  const dataTransfer = new DataTransfer();
  files.forEach((file) => file && dataTransfer.items.add(file));
  // Nothing found in the catalog (e.g. the solution's own script, already
  // uploaded) — there's nothing to upload.
  if (!dataTransfer.files.length) return;

  const fakeEvent = {
    target: { files: dataTransfer.files },
  } as unknown as React.ChangeEvent<HTMLInputElement>;

  await handleUpload(fakeEvent);
};

/**
 * `item.data`'s `.command` values, `.py`-suffixed — the scripts a Resource
 * Library row set needs uploaded. A native command (system or library, see
 * `stores/utils/action-node-names.ts`) is built into the platform and has no
 * script, so it is left out.
 */
export const scriptNamesFor = (
  data: { command?: string | null }[],
  nativeCommands: readonly string[] = tableStore.actionNodeNames
): string[] => {
  const native = new Set(nativeCommands);
  return data
    .map((row) => row.command?.trim())
    .filter((cmd): cmd is string => Boolean(cmd) && !native.has(cmd!))
    .map((cmd) => `${cmd}.py`);
};
